import React, { useEffect, useRef, useState } from 'react';
import { Edit2, Delete, Printer } from 'lucide-react';
import ExpenseDetailsScroll from './ExpenseDetailsScroll';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { accountService } from '../../services/accountService';
import DelgroProgressBar from '../../generals/DelgroProgressBar';
import { formatCustomNumber } from '../../helpers/formatCustomNumber';
import { GetExpenseListData, SetExpenseEdit } from '../../store/expensesAtom';
import GeneralModal from "../../helpers/GeneralModal";
import { createModal } from "react-modal-promise";
import StatusBadge from "../../generals/StatusBadge";

export default function ExpenseDetailsPage() {
  const expenseRef = useRef();
  const { id } = useParams();
  const [expDetails, setExpDetails] = useState(null);
  const [expenseLines, setExpenseLines] = useState([]);
  const [expenseHistory, setExpenseHistory] = useState([]);
  const [attachmentUrls, setAttachmentUrls] = useState([]);
  const setExpenseEdit = SetExpenseEdit();
  const myPromiseModal = createModal(GeneralModal);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const urlParts = pathname.split('/');
  const currModuleId = urlParts[1];
  const currModuleName = urlParts[2];
  const currUserMenu = urlParts[3];

  useEffect(() => {
    accountService.expenseDetails(id).then((res) => {
      const lines = res.data?.expense_info || [];
      setExpenseLines(lines);
      setExpDetails(lines[0] || null);
      setExpenseHistory(res.data?.journal_info || []);
      setAttachmentUrls(res.data?.attachment_url || []);
    });
  }, [id]);

  const totalAmount = expenseLines.reduce((sum, l) => sum + (l.Amount || 0), 0);

  const editExpense = async () => {
    const result = await myPromiseModal({
      title: "Confirm your action",
      body: 'Do you want to modify this expense?',
      showInput: false,
      placeHolder: "",
    });
    if (result.decision === true) {
      setExpenseEdit({ ...expDetails, lines: expenseLines, attachmentUrls });
      navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
    }
  };

  const deleteExpense = async (id) => {
    const result = await myPromiseModal({
      title: "Confirm Delete",
      body: `Expense #${id} will be permanently deleted.`,
      showInput: false,
      placeHolder: "",
    });
    if (result.decision === true) {
      accountService.deleteExpense(id).then(() => navigate(-1));
    }
  };

  const printSection = (elementRef, title = 'Print') => {
    const printContents = elementRef.current.innerHTML;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>${printContents}</body>
      </html>`;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  const viewJournalInfo = (accountRefNo) => {
    window.open(`/${currModuleId}/${currModuleName}/manual-journels/details/${accountRefNo}`, '_blank');
  };

  return (
    <section className="mt-2">
      {!expDetails && <DelgroProgressBar />}

      <div className="mt-4 lg:flex space-x-1">
        {/* Sidebar */}
        <div className="lg:w-[210px]">
          <ExpenseDetailsScroll />
        </div>

        {/* Main content */}
        <div className="w-full overflow-x-hidden mt-4 lg:mt-0 lg:pl-4">
          <div ref={expenseRef} className="bg-white min-h-screen rounded-xl border border-gray-100 shadow-sm">

            {/* ── Top header bar ── */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-0.5">
                  Expense #{expDetails?.Ref_No}
                </p>
                <h1 className="text-lg font-bold text-gray-900">
                  {expDetails?.Expense_Remark || 'Expense Details'}
                </h1>
              </div>
              <div className="flex items-center gap-2">
                {expDetails?.Status_Name && (
                  <StatusBadge
                    statusId={expDetails?.Status_Id}
                    statusName={expDetails?.Status_Name}
                    size="md"
                  />
                )}
              </div>
            </div>

            <div className="flex flex-col lg:flex-row">
              {/* ── Left panel ── */}
              <div className="flex-1 p-6 space-y-6">

                {/* Action buttons */}
                <div className="flex items-center gap-5">
                  <button onClick={editExpense} className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium">
                    <Edit2 className="w-4 h-4" /> Edit
                  </button>
                  <button onClick={() => deleteExpense(id)} className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 font-medium">
                    <Delete className="w-4 h-4" /> Delete
                  </button>
                  <button onClick={() => printSection(expenseRef, 'Expense')} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-800 font-medium">
                    <Printer className="w-4 h-4" /> Print
                  </button>
                </div>

                {/* Total amount */}
                <div className="bg-red-50 border border-red-100 rounded-xl px-5 py-4 inline-block">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">Total Expense</p>
                  <p className="text-3xl font-bold text-red-500">
                    ₦{formatCustomNumber(totalAmount)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">on {expDetails?.Expense_Date_String}</p>
                </div>

                {/* Header meta */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4 text-sm">
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Paid Through</p>
                    <p className="font-medium text-gray-800">{expDetails?.Paid_Account_Name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Vendor</p>
                    <p className="font-medium text-gray-800">{expDetails?.Vendor_Name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Reference</p>
                    <p className="font-medium text-gray-800">{expDetails?.Expense_Reference || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Encoded By</p>
                    <p className="font-medium text-gray-800">{expDetails?.Encoded_By_Name || '—'}</p>
                  </div>
                </div>

                {/* ── Expense Lines table ── */}
                <div>
                  <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Expense Lines</h2>
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          <th className="px-4 py-3 text-left">#</th>
                          <th className="px-4 py-3 text-left">Expense Account</th>
                          <th className="px-4 py-3 text-left">Tax Type</th>
                          <th className="px-4 py-3 text-left">Tax</th>
                          <th className="px-4 py-3 text-right">Amount (₦)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {expenseLines.map((line, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 transition">
                            <td className="px-4 py-3 text-gray-400">{idx + 1}</td>
                            <td className="px-4 py-3 font-medium text-gray-800">{line.Expense_Account_Name}</td>
                            <td className="px-4 py-3 text-gray-600">{line.Tax_Inclusive_Name || '—'}</td>
                            <td className="px-4 py-3 text-gray-600">{line.Tax_Name || '—'}</td>
                            <td className="px-4 py-3 text-right font-semibold text-gray-800">
                              {formatCustomNumber(line.Amount || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-50 border-t-2 border-gray-200">
                          <td colSpan={4} className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
                            Total
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-red-600">
                            ₦{formatCustomNumber(totalAmount)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* ── Journal Details table ── */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Journal Details</h2>
                    <button
                      onClick={() => viewJournalInfo(expDetails?.Account_Ref_No)}
                      className="text-xs text-blue-600 hover:underline font-medium"
                    >
                      Account Ref: {expDetails?.Account_Ref_No}
                    </button>
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-purple-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-purple-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          <th className="px-4 py-3 text-left">Account</th>
                          <th className="px-4 py-3 text-right">Debit (₦)</th>
                          <th className="px-4 py-3 text-right">Credit (₦)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {expenseHistory.map((jnl, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 transition">
                            <td className="px-4 py-3 font-medium text-gray-800">{jnl.Account_Name}</td>
                            <td className="px-4 py-3 text-right text-gray-700">
                              {jnl.Debit_Amount > 0 ? formatCustomNumber(jnl.Debit_Amount) : '—'}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-700">
                              {jnl.Credit_Amount > 0 ? formatCustomNumber(jnl.Credit_Amount) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>

              {/* ── Right panel — attachments ── */}
              <div className="w-full lg:w-72 bg-gray-50 p-6 border-t lg:border-t-0 lg:border-l border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                  Attachments {attachmentUrls.length > 0 && <span className="ml-1 text-gray-400 font-normal">({attachmentUrls.length})</span>}
                </p>

                {attachmentUrls.length === 0 ? (
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 bg-white text-center text-gray-400">
                    <svg className="w-6 h-6 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="text-xs">No attachments</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {attachmentUrls.map((item, idx) => {
                      const url = item.Attachment_URL;
                      const filename = url?.split('/').pop() || `File ${idx + 1}`;
                      const isImage = /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(url);
                      return (
                        <a
                          key={idx}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex flex-col rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm hover:border-blue-300 hover:shadow-md transition-all"
                        >
                          {isImage ? (
                            <div className="h-32 bg-gray-100 overflow-hidden">
                              <img
                                src={url}
                                alt={filename}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                          ) : (
                            <div className="h-20 bg-gray-50 flex items-center justify-center">
                              <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                          <div className="px-3 py-2 flex items-center justify-between gap-2">
                            <p className="text-xs text-gray-500 truncate flex-1" title={filename}>{filename}</p>
                            <svg className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
